/**
 * Safety Report types and display constants
 * Shared between OpsPage widget and SafetyReportsPage
 */

export type SafetyReportStatus = 'OPEN' | 'UNDER_REVIEW' | 'CLOSED';
export type HazardCategory = 'WEATHER' | 'MECHANICAL' | 'ATC' | 'GROUND_OPS' | 'HUMAN_FACTORS' | 'BIRD_STRIKE' | 'RUNWAY_INCURSION' | 'CABIN_SAFETY' | 'OTHER';
export type RiskSeverity = 'NEGLIGIBLE' | 'MINOR' | 'MAJOR' | 'HAZARDOUS' | 'CATASTROPHIC';
export type RiskLikelihood = 'IMPROBABLE' | 'REMOTE' | 'OCCASIONAL' | 'PROBABLE' | 'FREQUENT';

export interface SafetyReport {
  id: number;
  anonymous: boolean;
  flightId?: number | null;
  tripId?: number | null;
  tailNumber?: string | null;
  fromAirport?: string | null;
  toAirport?: string | null;
  flightDate?: string | null;
  narrative: string;
  hazardCategory: HazardCategory;
  severity: RiskSeverity;
  likelihood: RiskLikelihood;
  actionTaken: string;
  recommendedFollowUp?: string | null;
  status: SafetyReportStatus;
  adminNotes?: string | null;
  closedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  reporter?: { firstName: string; lastName: string } | null;
  reviewedBy?: { firstName: string; lastName: string } | null;
  trip?: { qNumber: string } | null;
}

export interface SafetyStats {
  open: number;
  underReview: number;
  closed: number;
  total: number;
}

export const HAZARD_LABELS: Record<HazardCategory, string> = {
  WEATHER: 'Weather',
  MECHANICAL: 'Mechanical',
  ATC: 'ATC',
  GROUND_OPS: 'Ground Ops',
  HUMAN_FACTORS: 'Human Factors',
  BIRD_STRIKE: 'Bird Strike',
  RUNWAY_INCURSION: 'Runway Incursion',
  CABIN_SAFETY: 'Cabin Safety',
  OTHER: 'Other',
};

export const SEVERITY_COLORS: Record<RiskSeverity, string> = {
  NEGLIGIBLE: 'bg-green-500',
  MINOR: 'bg-yellow-500',
  MAJOR: 'bg-orange-500',
  HAZARDOUS: 'bg-red-500',
  CATASTROPHIC: 'bg-red-700',
};

export const STATUS_BADGE_CLASSES: Record<SafetyReportStatus, string> = {
  OPEN: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  UNDER_REVIEW: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  CLOSED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
};

export const STATUS_LABELS: Record<SafetyReportStatus, string> = {
  OPEN: 'Open',
  UNDER_REVIEW: 'Under Review',
  CLOSED: 'Closed',
};

export function getRiskLevel(severity: RiskSeverity, likelihood: RiskLikelihood): 'low' | 'medium' | 'high' | 'critical' {
  const sevIndex = ['NEGLIGIBLE', 'MINOR', 'MAJOR', 'HAZARDOUS', 'CATASTROPHIC'].indexOf(severity);
  const likIndex = ['IMPROBABLE', 'REMOTE', 'OCCASIONAL', 'PROBABLE', 'FREQUENT'].indexOf(likelihood);
  const score = (sevIndex + 1) * (likIndex + 1);
  if (score <= 4) return 'low';
  if (score <= 9) return 'medium';
  if (score <= 16) return 'high';
  return 'critical';
}

export const RISK_LEVEL_CLASSES: Record<string, string> = {
  low: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};
